import { RootState } from '@AppState/redux-store'
import { ApiKeysServiceAPI } from '@API/data-services/api-keys-service'
import { BackOfficeApiKeyCreateResponse, ServiceName } from '@Generated/back-office-client/src'
import { Button, Callout, CheckboxGroup, Flex, Select, Text, TextField } from '@radix-ui/themes'
import { Form } from 'radix-ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { KeyRevealModal } from './api-key-dialogs'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Routes } from '@Components/main-app/types'
import { NO_SCOPES_MESSAGE, useAvailableScopes } from './use-available-scopes'

const SERVICE_OPTIONS: { label: string; value: ServiceName }[] = [
  { label: 'Game Data Service (GDS)', value: ServiceName.GDS },
  { label: 'Player Data Service (PDS)', value: ServiceName.PDS },
  { label: 'RNG Service (RNG)', value: ServiceName.RNG },
]

export const CreateApiKey = () => {
  const nav = useNavigate()
  const queryClient = useQueryClient()
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const [selectedService, setSelectedService] = useState<ServiceName>(ServiceName.GDS)
  const [selectedScopes, setSelectedScopes] = useState<string[]>([])
  const [scopesError, setScopesError] = useState(false)
  const [createdKey, setCreatedKey] = useState<BackOfficeApiKeyCreateResponse | null>(null)

  const { availableScopes, scopesLoading, scopesErrorMessage } = useAvailableScopes(selectedService)

  useEffect(() => {
    setSelectedScopes([])
    setScopesError(false)
  }, [selectedService])

  const [createError, setCreateError] = useState<string | null>(null)

  const { mutate: createKey, isPending } = useMutation({
    mutationFn: ({
      description,
      scopes,
      service,
    }: {
      description: string
      scopes: string[]
      service: ServiceName
    }) => {
      return ApiKeysServiceAPI.createKey(description, scopes, service)
    },
    onSuccess: (response) => {
      setCreateError(null)
      setCreatedKey(response)
      queryClient.invalidateQueries({ queryKey: [`${activeCustomerId}-api-keys`] })
    },
    onError: () => {
      setCreateError('Failed to create API key')
    },
  })

  const handleDismissModal = () => {
    setCreatedKey(null)
    nav(Routes.API_KEYS)
  }

  return (
    <>
      <Form.Root
        onSubmit={(e) => {
          e.preventDefault()
          const formData = new FormData(e.currentTarget).entries()
          const data = Object.fromEntries(formData)

          if (!selectedScopes.length) {
            setScopesError(true)
            return
          }

          createKey({
            description: data.description as string,
            scopes: selectedScopes,
            service: selectedService,
          })
        }}>
        <Flex direction="column" gap="4">
          <Form.Field name="description">
            <Flex justify="between">
              <Form.Label>Description</Form.Label>
              <Form.Message className="FormMessage" match="valueMissing">
                Description is required
              </Form.Message>
            </Flex>

            <Form.Control asChild required>
              <TextField.Root placeholder="e.g. Production back-office key" />
            </Form.Control>
          </Form.Field>

          <Flex direction="column" gap="1">
            <Text as="label" size="2" weight="bold">
              Service
            </Text>
            <Select.Root
              value={selectedService}
              onValueChange={(value) => setSelectedService(value as ServiceName)}>
              <Select.Trigger />
              <Select.Content>
                {SERVICE_OPTIONS.map((opt) => (
                  <Select.Item key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>

          <Form.Field name="scopes">
            <Flex justify="between">
              <Form.Label>Scopes</Form.Label>
              {scopesError ? (
                <Form.Message className="FormMessage">At least one scope is required</Form.Message>
              ) : null}
            </Flex>

            {scopesLoading ? (
              <Text size="2" color="gray">
                Loading scopes...
              </Text>
            ) : availableScopes?.length ? (
              <CheckboxGroup.Root
                value={selectedScopes}
                onValueChange={(checkedValues) => {
                  setSelectedScopes(checkedValues)
                  if (checkedValues.length) {
                    setScopesError(false)
                  }
                }}>
                {availableScopes.map((scope) => (
                  <CheckboxGroup.Item key={scope} value={scope}>
                    {scope}
                  </CheckboxGroup.Item>
                ))}
              </CheckboxGroup.Root>
            ) : (
              <Text size="2" color={scopesErrorMessage ? 'red' : 'gray'}>
                {scopesErrorMessage ?? NO_SCOPES_MESSAGE}
              </Text>
            )}
          </Form.Field>

          {createError ? (
            <Callout.Root color="red">
              <Callout.Text>{createError}</Callout.Text>
            </Callout.Root>
          ) : null}

          <Flex gap="3">
            <Form.Submit asChild>
              <Button loading={isPending} style={{ cursor: 'pointer' }}>
                Create API Key
              </Button>
            </Form.Submit>
            <Button
              variant="soft"
              color="gray"
              style={{ cursor: 'pointer' }}
              onClick={() => nav(Routes.API_KEYS)}>
              Cancel
            </Button>
          </Flex>
        </Flex>
      </Form.Root>

      {createdKey ? (
        <KeyRevealModal
          title="API Key Created"
          encodedKey={createdKey.encodedKey}
          onDismiss={handleDismissModal}
        />
      ) : null}
    </>
  )
}
